import { memo, ReactNode, useMemo } from 'react';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import Button from '@/components/Button/Button';
import Card from '@/components/Card/Card';
import Paragraph from '@/components/Paragraph/Paragraph';
import AttentionLottie from '@/assets/lottie/attention.json';
import DoneLottie from '@/assets/lottie/done.json';
import styles from './StatusCard.module.scss';
import H1 from '@/components/H/H1';

export type Props = {
  type?: 'success' | 'rejected';
  heading?: string;
  description?: string;
  children?: ReactNode;
  childrenBeforeBtn?: ReactNode;
  onClick?: () => void;
  button?: string;
  isButtonPending?: boolean;
  className?: string;
  dataTest?: string;
  hideButton?: boolean;
  disabled?: boolean;
  headingClassName?: string;
  descriptionClassName?: string;
  buttonClassName?: string;
  buttonTextClassName?: string;
};

const StatusCardMemo = memo(
  ({
    type = 'success',
    heading,
    childrenBeforeBtn,
    children,
    description = '',
    button,
    isButtonPending = false,
    onClick,
    className,
    dataTest = 'statusCard',
    hideButton = false,
    disabled = false,
    headingClassName,
    descriptionClassName,
    buttonClassName,
    buttonTextClassName,
  }: Props) => {
    const { t } = useTranslation('common');

    const lottieOptions = useMemo(
      () => ({
        loop: false,
        autoplay: true,
        animationData: type === 'success' ? DoneLottie : AttentionLottie,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice',
        },
      }),
      [type],
    );

    const cardStyle = type === 'success' ? styles['card-success'] : styles.card;

    return (
      <Card className={classes(cardStyle, className)} dataTest={dataTest}>
        <div className={styles.content}>
          <div
            className={styles['lottie-wrapper']}
            data-test={`${dataTest}-lottie-wrapper`}
          >
            <Lottie options={lottieOptions} />
          </div>
          {heading && (
            <H1 className={classes(styles.heading, headingClassName)}>
              <span
                dangerouslySetInnerHTML={{ __html: heading }}
                data-test={`${dataTest}-heading`}
              />
            </H1>
          )}

          {description && (
            <Paragraph
              className={classes(styles.description, descriptionClassName)}
            >
              <span
                dangerouslySetInnerHTML={{ __html: description }}
                data-test={`${dataTest}-description`}
              />
            </Paragraph>
          )}
        </div>
        {childrenBeforeBtn}
        {onClick && !hideButton && (
          <Button
            dataTest="continue"
            className={classes(styles.button, buttonClassName)}
            buttonClassName={buttonTextClassName}
            onClick={onClick}
            pending={isButtonPending}
            disabled={disabled}
          >
            {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              button || t('understood')
            }
          </Button>
        )}
        {children}
      </Card>
    );
  },
);

export default StatusCardMemo;
